import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useContext, useEffect, useRef, useState } from 'react';
import { sendFileToCustomEndpoint, uploadFile } from '../../API/api';
import captureIcon from '../../Assets/capture-icon.svg';
import switchCameraIcon from '../../Assets/switch-camera.svg';
import { titleFromParams } from '../../Utilities/helperFunction';
import appContext from '../../appContext';
import '../Linkedin/style.scss';
import './style.scss';

const CameraCapture = ({
    mock,
    index,
    setValue,
    maintainValues,
    setMaintainValues,
}) => {
    const { setCustomStyles } = useContext(appContext);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [stream, setStream] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [videoDevices, setVideoDevices] = useState([]);
    const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Open camera and request permissions
    const openCamera = async () => {
        try {
            setError('');
            setIsCameraOpen(true);

            const constraints = {
                video: videoDevices.length
                    ? { deviceId: { exact: videoDevices[currentDeviceIndex]?.deviceId } }
                    : { facingMode: 'user' },
                audio: false
            };

            const mediaStream = await navigator?.mediaDevices?.getUserMedia(constraints);
            setStream(mediaStream);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            }, 100);
        } catch (err) {
            console.error('Error accessing camera:', err);
            setError('Unable to access camera. Please grant camera permissions.');
        }
    };

    // Capture image from video stream
    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
            setCapturedImage(imageDataUrl);
            stopCamera();
        }
    };

    // Retake photo
    const retakePhoto = () => {
        setCapturedImage(null);
        openCamera();
    };

    const switchCamera = () => {
        if (!videoDevices.length) return;
        stopCamera();
        setCurrentDeviceIndex(prev => (prev + 1) % videoDevices.length);
        openCamera();
    };

    // Confirm and upload
    const confirmPhoto = async () => {
        if (capturedImage) {
            try {
                setIsLoading(true);
                const base64Response = await fetch(capturedImage);
                const blob = await base64Response.blob();
                const file = new File([blob], `captured-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

                let formData = new FormData();
                formData.append('file', file);

                const fileRes = await uploadFile(formData, atob(sessionStorage?.formUUID), sessionStorage?.principalId);

                if (fileRes?.url && fileRes?.url?.length) {
                    setValue(mock?.name, fileRes.url);
                    setMaintainValues({ ...maintainValues, [`${mock?.name}`]: fileRes.url });
                    setIsLoading(false);
                    closeCamera();
                } else {
                    setIsLoading(false);
                    setError('Failed to upload image');
                }

                if (mock?.sendFileToApi && mock?.postApiDetails?.endpoint) {
                    await sendFileToCustomEndpoint(mock?.postApiDetails?.endpoint, formData);
                }
            } catch (error) {
                console.error('Error uploading captured image:', error);
                setIsLoading(false);
                setError('Failed to upload captured photo');
            }
        }
    };

    // Stop camera stream
    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    // Close camera
    const closeCamera = () => {
        stopCamera();
        setIsCameraOpen(false);
        setCapturedImage(null);
        setError('');
    };

    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, [stream]);

    useEffect(() => {
        setCustomStyles(mock?.styles || {});
        navigator.mediaDevices.enumerateDevices().then(devices => {
            const videoInputs = devices.filter(d => d?.kind === "videoinput");
            const filteredDevices = videoInputs.filter(d => d?.label !== "OBS Virtual Camera");
            setHasMultipleCameras(filteredDevices.length > 1);
            setVideoDevices(filteredDevices);
        });
    }, []);

    return (
        <div className="question-container">
            <div className="question-title">
                <h1 className="title">
                    <span>
                        {index}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="11"
                            height="10"
                            viewBox="0 0 24 24"
                        >
                            <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z" />
                        </svg>
                    </span>
                    <div
                        dangerouslySetInnerHTML={{ __html: titleFromParams(mock?.title) }}
                    />
                </h1>
                <div>
                    <p>{mock?.description}</p>
                </div>
            </div>

            {maintainValues[mock?.name] ? (
                <div className="captured-display">
                    <img src={maintainValues[mock?.name]} alt="Captured" className="captured-thumbnail" />
                    <button className="recapture-btn" type="button" onClick={openCamera}>
                        Retake Photo
                    </button>
                </div>
            ) : (
                <button className="camera-trigger-btn" type="button" onClick={openCamera}>
                    <CameraAltIcon />
                    Capture Photo
                </button>
            )}

            {isCameraOpen && (
                <div className="camera-modal-overlay">
                    <div className="camera-modal">
                        <div className="camera-header">
                            <h3>{isLoading ? 'Uploading...' : 'Capture Photo'}</h3>
                            <button className="close-btn" type="button" onClick={closeCamera} disabled={isLoading}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 96 960 960" width="24" fill="currentColor">
                                    <path d="M256 760l224-224 224 224 56-56-224-224 224-224-56-56-224 224-224-224-56 56 224 224-224 224 56 56Z" />
                                </svg>
                            </button>
                        </div>


                        <div className="camera-content">
                            {error && (
                                <div className="camera-error">
                                    <p>{error}</p>
                                </div>
                            )}
                            {!capturedImage ? (
                                <>
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        className="camera-video"
                                    />
                                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                                    <div className="camera-controls">
                                        {hasMultipleCameras && (
                                            <button className="switch-camera-btn" type="button" onClick={switchCamera}>
                                                <img src={switchCameraIcon} alt="Switch Camera" />
                                            </button>
                                        )}
                                        <button className="capture-btn" type="button" onClick={capturePhoto} disabled={isLoading || error}>
                                            <img src={captureIcon} alt="Capture" />
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <img src={capturedImage} alt="Captured" className="captured-photo" />

                                    <div className="preview-controls">
                                        <button className="retake-btn" type="button" onClick={retakePhoto} disabled={isLoading}>
                                            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 96 960 960" width="20" fill="currentColor">
                                                <path d="M451 957q-123-10-207-101t-84-216q0-77 35.5-145T295 373l43 43q-56 40-87 101t-31 131q0 104 67 182.5T451 896v61Zm58 0v-61q92-8 159-86.5T735 627q0-112-78.5-190.5T466 358h-11l60 60-43 43-133-133 133-133 43 43-60 60h11q66 0 124.5 25t102 68.5Q738 437 763.5 495T789 619q0 125-84 216T509 957Z" />
                                            </svg>
                                            Retake
                                        </button>
                                        <button className="confirm-btn" type="button" onClick={confirmPhoto} disabled={isLoading}>
                                            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 96 960 960" width="20" fill="currentColor">
                                                <path d="M378 810 154 586l43-43 181 181 384-384 43 43-427 427Z" />
                                            </svg>
                                            {isLoading ? 'Uploading...' : 'Confirm'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CameraCapture;
